/**
 * Browser-first game analysis (SF18-lite, single-threaded Worker pool).
 *
 * Produces the same `AnalysisResponse` / `game_moves` stream the backend SSE
 * emits, while keeping server-parity logic in dependency-free helpers.
 */

import {Observable} from 'rxjs';
import {Chess} from 'chess.js';
import * as SentryReact from '@sentry/react';
import ChessRestClient from '@/Api/ChessRestClient';
import {isEngineSupported} from '@/Components/StockfishEngine/engine';
import {
  EnginePool,
  type AnalyzeFenOptions,
  type EngineEvalLine,
  type PositionEval,
} from '@/Components/StockfishEngine/enginePool';
import {
  accuracy,
  analysisCacheKey,
  classify,
  classifyPuzzle,
  selectBestResponses,
  shouldDeepen,
  toUserScore,
  type Classification,
} from './analysisParity';
import {getCached, putCached} from './analysisCache';
import {reportBrowserAnalysisEvent} from './browserAnalysisTelemetry';
import {classifyFailure} from './browserAnalysisFailure';
import {PER_POSITION_TIMEOUT_MESSAGE} from '@/Components/StockfishEngine/enginePool';

const ENGINE_VERSION = 'sf18-lite-single';
const DB_VARCHAR_100_MAX = 100;
const MAX_BROWSER_CACHE_LINES = 4;
const MAX_BROWSER_CACHE_PV_MOVES = 20;
const MATE_SCORE_CP = 99999;
const DB_TEXT_100_FIELDS = [
  'best_current_move',
  'best_possible_response',
  'best_response',
  'reason',
  'alternate_move',
  'alternate_moves',
] as const;
const DEEP_DEVICE_PROFILE_KEY =
  'cc-web-learning:browser-analysis:deep-device-profile:v4';
const SERVER_PARITY_DEPTH = 30;
const SERVER_PARITY_MOVETIME_MS = 3000;
const SERVER_PARITY_MULTIPV = 4;
const MIN_DEEPEN_DEPTH = SERVER_PARITY_DEPTH;

export type BrowserAnalysisMode = 'normal' | 'deep';

const ANALYSIS_CONFIGS: Record<
  BrowserAnalysisMode,
  {configHash: string; screen: AnalyzeFenOptions; deepen: AnalyzeFenOptions}
> = {
  normal: {
    configHash: 'normal-adaptive-d9-12_d14-16-pv3_v1',
    screen: {depth: 18, movetime: 450, multiPV: 1, timeoutMs: 7000},
    deepen: {depth: 24, movetime: 1400, multiPV: 3, timeoutMs: 9000},
  },
  deep: {
    configHash: 'deep-server-parity-d30-t3000-pv4-v1',
    screen: {
      depth: SERVER_PARITY_DEPTH,
      movetime: SERVER_PARITY_MOVETIME_MS,
      multiPV: SERVER_PARITY_MULTIPV,
      timeoutMs: 20000,
    },
    deepen: {
      depth: SERVER_PARITY_DEPTH,
      movetime: SERVER_PARITY_MOVETIME_MS,
      multiPV: SERVER_PARITY_MULTIPV,
      timeoutMs: 20000,
    },
  },
};

type DeepDeviceProfile = {
  deepenDepth: number;
};

function canUseLocalStorage(): boolean {
  // Presence is not enough: Safari private mode exposes localStorage but throws
  // on setItem. Probe with a real write/remove.
  try {
    if (typeof localStorage === 'undefined') {
      return false;
    }
    const probeKey = '__cc_ls_probe__';
    localStorage.setItem(probeKey, '1');
    localStorage.removeItem(probeKey);
    return true;
  } catch {
    return false;
  }
}

function clampDepth(
  value: unknown,
  fallback: number,
  min: number,
  max: number,
) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return fallback;
  }
  return Math.max(min, Math.min(max, Math.floor(n)));
}

function readDeepDeviceProfile(): DeepDeviceProfile {
  const defaults = {
    deepenDepth: ANALYSIS_CONFIGS.deep.deepen.depth || 30,
  };
  if (!canUseLocalStorage()) {
    return defaults;
  }
  try {
    const raw = localStorage.getItem(DEEP_DEVICE_PROFILE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      deepenDepth: clampDepth(
        parsed?.deepenDepth,
        defaults.deepenDepth,
        MIN_DEEPEN_DEPTH,
        defaults.deepenDepth,
      ),
    };
  } catch {
    return defaults;
  }
}

function writeDeepDeviceProfile(profile: DeepDeviceProfile): void {
  if (!canUseLocalStorage()) {
    return;
  }
  try {
    localStorage.setItem(DEEP_DEVICE_PROFILE_KEY, JSON.stringify(profile));
  } catch {
    /* best-effort device tuning */
  }
}

function deepConfigHash(profile: DeepDeviceProfile): string {
  return `${ANALYSIS_CONFIGS.deep.configHash}_dd${profile.deepenDepth}`;
}

export type AnalysisResponse = {
  game_id?: number;
  game_uuid?: number | string;
  status?: string;
  puzzle_summary?: any;
  success?: boolean;
  game_moves?: any[];
  error?: string;
  players?: any;
  game_details?: any;
  game_overview?: any;
  game_result?: string;
  game_reviewed_by_user?: boolean;
  game_engine_analyzed?: boolean;
  total_moves?: number;
  move_index?: number;
  analysis?: any;
  game_score_details?: any;
  analysis_mode?: BrowserAnalysisMode;
  analysis_progress?: number;
  progress_message?: string;
  current_position?: number;
  total_positions?: number;
  source?: 'browser';
};

interface GameMeta {
  pgn: string;
  color: 'white' | 'black';
  username: string;
  opponent: string;
  player_rating?: number;
  opponent_rating?: number;
  time_control?: string;
  time_class?: string;
  uuid?: string;
  userid?: number;
  platform?: string;
  /** CCGame.analyzed: 0 = not analyzed, 1 = analyzed, 2 = stream API only. */
  analyzed?: number;
  raw: any;
}

interface Ply {
  ply: number;
  moveIndex: number;
  color: 'white' | 'black';
  san: string;
  fenBefore: string;
  fenAfter: string;
  timeLeftOnClock?: number;
  timeTakenInSecs?: number;
  averageTime?: number;
}

type StoredEval = {
  userScore: number;
  bestUci: string;
  pvSan: string[];
  lines: EngineEvalLine[];
  stm: 'w' | 'b';
};

type MoveBuild = {
  moveFields: any;
  cls: Classification;
  puzzle: ReturnType<typeof classifyPuzzle>;
};

async function fetchGameMeta(
  gameId: number | string,
): Promise<GameMeta | null> {
  const resp = await ChessRestClient.post('game/get_games', {game_id: gameId});
  const data = extractGameData(resp, gameId);
  const pgn: string = data?.pgn || '';
  if (!pgn) {
    return null;
  }
  return {
    pgn,
    color:
      String(data?.color || 'white').toLowerCase() === 'black'
        ? 'black'
        : 'white',
    username: data?.username || 'Me',
    opponent: data?.opponent_username || 'opponent',
    player_rating: data?.rating,
    opponent_rating: data?.opponent_rating,
    time_control: data?.time_control,
    time_class: data?.time_class,
    uuid: data?.uuid,
    userid: data?.userid,
    platform: data?.platform,
    analyzed: typeof data?.analyzed === 'number' ? data.analyzed : undefined,
    raw: data,
  };
}

function extractGameData(resp: any, gameId: number | string): any {
  const root = resp?.data;
  const candidates = [root?.data?.data, root?.data, root];
  const matchesGameId = (game: any) =>
    [game?.id, game?.game_id, game?.uuid].some(
      value => value != null && String(value) === String(gameId),
    );

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      const exact = candidate.find(game => game?.pgn && matchesGameId(game));
      if (exact) {
        return exact;
      }
      const withPgn = candidate.find(game => game?.pgn);
      if (withPgn) {
        return withPgn;
      }
    } else if (candidate?.pgn) {
      return candidate;
    }
  }

  return null;
}

export async function fetchBrowserAnalysisPlyCount(
  gameId: number | string,
): Promise<number> {
  const meta = await fetchGameMeta(gameId);
  if (!meta) {
    return 0;
  }
  return parsePliesFromPgn(meta.pgn).length;
}

/**
 * True when an analysis is already stored for this game — either a full server
 * analysis (`CCGame.analyzed === 1`) OR a previously persisted browser analysis
 * (`analyzed === 2`, written by `store_browser_game_analysis`). The router uses
 * this to bypass browser re-analysis and reuse the stored moves: from the
 * localStorage browser cache when present, else streamed from the backend.
 * Recognizing `analyzed === 2` is what stops a game from being re-analyzed when
 * the localStorage cache is gone (cleared, or a different device/browser).
 * Treats network/fetch failures as "unknown" (false) so we never block on a
 * metadata miss.
 */
export async function isGameAlreadyAnalyzed(
  gameId: number | string,
): Promise<boolean> {
  try {
    const meta = await fetchGameMeta(gameId);
    const analyzed = Number(meta?.analyzed ?? 0);
    return analyzed === 1 || analyzed === 2;
  } catch {
    return false;
  }
}

export async function getCachedBrowserAnalysis(
  gameId: number | string,
  mode: BrowserAnalysisMode = 'normal',
): Promise<AnalysisResponse[] | null> {
  // Metadata is only needed to recover the uuid the cache key was written under.
  // A metadata failure must NOT prevent a localStorage hit, so treat it as
  // "no uuid" and still probe the gameId-keyed entry. Probe both the uuid- and
  // gameId-based keys since `analyzeGameInBrowser` writes under `uuid || gameId`.
  let uuid: string | number | undefined;
  try {
    uuid = (await fetchGameMeta(gameId))?.uuid;
  } catch {
    uuid = undefined;
  }
  const configHash =
    mode === 'deep'
      ? deepConfigHash(readDeepDeviceProfile())
      : ANALYSIS_CONFIGS[mode].configHash;
  const candidateIds = uuid && uuid !== gameId ? [uuid, gameId] : [gameId];
  for (const id of candidateIds) {
    const cached = await getCached(
      analysisCacheKey(id, ENGINE_VERSION, configHash),
    );
    if (cached?.length) {
      return cached;
    }
  }
  return null;
}

/**
 * POST the browser-computed analysis to the chess backend so it is persisted
 * exactly like a server analysis (reuses `write_game_evaluation_response_to_db`
 * via the new `game/store_browser_game_analysis` endpoint). Fire-and-forget:
 * persistence failures must never break the UX since the user already saw the
 * analysis locally.
 */
async function persistBrowserAnalysisToBackend(
  gameUuid: string | number | undefined,
  gameMoves: any[],
  computedAccuracy: number,
  opponentAccuracy: number,
  analysisMode: BrowserAnalysisMode,
  browserEngineCache: BrowserEngineCacheEntry[] = [],
): Promise<void> {
  if (!gameUuid || !gameMoves.length) {
    return;
  }
  try {
    const response = await ChessRestClient.post(
      'game/store_browser_game_analysis',
      {
        game_uuid: gameUuid,
        game_moves: toPersistableGameMoves(gameMoves),
        accuracy: computedAccuracy,
        opponent_accuracy: opponentAccuracy,
        engine_version: ENGINE_VERSION,
        analysis_mode: analysisMode,
        browser_engine_cache:
          analysisMode === 'deep' ? browserEngineCache : undefined,
      },
    );
    if (response?.data?.success === false) {
      throw new Error(
        response?.data?.error ||
          'browser deep analysis persist returned failure',
      );
    }
  } catch (err) {
    console.log('[gameAnalysis] persist to backend failed (non-fatal):', err);
    if (analysisMode === 'deep') {
      SentryReact.captureException(
        err instanceof Error
          ? err
          : new Error('browser deep analysis persist failed'),
        {
          tags: {
            feature: 'browser_deep_analysis',
            stage: 'frontend_persist_to_chess',
          },
          extra: {
            gameUuid,
            moves: gameMoves.length,
            cacheEntries: browserEngineCache.length,
            engineVersion: ENGINE_VERSION,
          },
        },
      );
    }
  }
}

type BrowserEngineCacheLine = {
  rank: number;
  kind: 'cp' | 'mate';
  val: number;
  depth: number;
  pv_uci: string[];
};

type BrowserEngineCacheEntry = {
  ply_index: number;
  move_index: number;
  move_color: 'white' | 'black';
  fen: string;
  side_to_move: 'w' | 'b';
  engine_version: string;
  lines: BrowserEngineCacheLine[];
};

function buildBrowserEngineCache(
  plies: Ply[],
  evals: StoredEval[],
  mode: BrowserAnalysisMode,
): BrowserEngineCacheEntry[] {
  if (mode !== 'deep') {
    return [];
  }
  return plies
    .map((ply, idx) => {
      const plyIndex = idx + 1;
      const stored = evals[plyIndex];
      const lines = (stored?.lines || [])
        .filter(line => Array.isArray(line.pvUci) && line.pvUci.length)
        .map(line => ({
          rank: line.rank,
          kind: line.kind,
          val: line.val,
          depth: line.depth,
          pv_uci: line.pvUci.slice(0, MAX_BROWSER_CACHE_PV_MOVES),
        }))
        .slice(0, MAX_BROWSER_CACHE_LINES);
      if (!stored || !lines.length) {
        return null;
      }
      return {
        ply_index: plyIndex,
        move_index: ply.moveIndex,
        move_color: ply.color,
        fen: ply.fenAfter,
        side_to_move: stored.stm,
        engine_version: ENGINE_VERSION,
        lines,
      };
    })
    .filter(Boolean) as BrowserEngineCacheEntry[];
}

function truncateDbText(value: unknown): unknown {
  if (typeof value !== 'string') {
    return value;
  }
  return value.length > DB_VARCHAR_100_MAX
    ? value.slice(0, DB_VARCHAR_100_MAX)
    : value;
}

function toPersistableGameMoves(gameMoves: any[]): any[] {
  return gameMoves.map(move => {
    const persistedMove = {...move};
    DB_TEXT_100_FIELDS.forEach(field => {
      persistedMove[field] = truncateDbText(persistedMove[field]);
    });
    if (Array.isArray(persistedMove.best_responses)) {
      persistedMove.best_responses = persistedMove.best_responses.map(
        response => truncateDbText(response),
      );
    }
    return persistedMove;
  });
}

function parseClockSeconds(clock: string): number | undefined {
  const parts = String(clock || '')
    .trim()
    .split(':')
    .map(part => Number(part));
  if (parts.some(part => !Number.isFinite(part))) {
    return undefined;
  }
  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return undefined;
}

function extractClockSecondsFromPgn(pgn: string): Array<number | undefined> {
  const clocks: Array<number | undefined> = [];
  const clockRegex = /\[%%?clk\s+([^\]\s]+)\]/g;
  let match: RegExpExecArray | null;
  while ((match = clockRegex.exec(String(pgn || ''))) !== null) {
    clocks.push(parseClockSeconds(match[1]));
  }
  return clocks;
}

function parseTimeControl(timeControl: string): {
  initialSeconds?: number;
  incrementSeconds: number;
} {
  const normalized = String(timeControl || '').replace(/\s+/g, '');
  if (!normalized || normalized === '-') {
    return {incrementSeconds: 0};
  }
  const lastSegment = normalized.split(':').pop() || normalized;
  const afterMoves = lastSegment.includes('/')
    ? lastSegment.slice(lastSegment.lastIndexOf('/') + 1)
    : lastSegment;
  const [baseRaw, incrementRaw] = afterMoves.split('+');
  const initialSeconds = Number(baseRaw);
  const incrementSeconds = Number(incrementRaw || 0);
  return {
    initialSeconds: Number.isFinite(initialSeconds)
      ? initialSeconds
      : undefined,
    incrementSeconds: Number.isFinite(incrementSeconds) ? incrementSeconds : 0,
  };
}

export function parsePliesFromPgn(pgn: string): Ply[] {
  const chess = new Chess();
  const normalizedPgn = String(pgn).replace(/\r/g, '').trim();
  chess.loadPgn(normalizedPgn, {strict: false} as any);
  const headers = chess.header?.() || {};
  const variant = String(headers.Variant || '')
    .trim()
    .toLowerCase();
  if (variant && variant !== 'standard' && variant !== 'from position') {
    throw new Error(`browser analysis: unsupported variant ${headers.Variant}`);
  }
  if (variant === 'from position' && !headers.FEN) {
    throw new Error('browser analysis: from-position PGN missing FEN');
  }
  const history = chess.history({verbose: true}) as any[];
  const clockSeconds = extractClockSecondsFromPgn(normalizedPgn);
  const timeControl = parseTimeControl(headers.TimeControl || '');
  const previousClockByColor: Partial<Record<'white' | 'black', number>> = {};
  const totalsByColor: Record<'white' | 'black', {sum: number; count: number}> =
    {
      white: {sum: 0, count: 0},
      black: {sum: 0, count: 0},
    };

  return history.map((mv, i) => {
    const color = mv?.color === 'w' ? 'white' : 'black';
    const timeLeftOnClock = clockSeconds[i];
    const previousClock =
      previousClockByColor[color] ?? timeControl.initialSeconds;
    let timeTakenInSecs: number | undefined;
    if (
      typeof previousClock === 'number' &&
      typeof timeLeftOnClock === 'number'
    ) {
      timeTakenInSecs = Math.max(
        0,
        previousClock + timeControl.incrementSeconds - timeLeftOnClock,
      );
      previousClockByColor[color] = timeLeftOnClock;
      totalsByColor[color].sum += timeTakenInSecs;
      totalsByColor[color].count += 1;
    }
    const averageTime =
      totalsByColor[color].count > 0
        ? Math.floor(
            (totalsByColor[color].sum / totalsByColor[color].count) * 10,
          ) / 10
        : undefined;
    return {
      ply: i + 1,
      moveIndex: Math.ceil((i + 1) / 2),
      color,
      san: mv?.san,
      fenBefore: mv?.before,
      fenAfter: mv?.after,
      timeLeftOnClock,
      timeTakenInSecs,
      averageTime,
    };
  });
}

function stmOf(fen: string): 'w' | 'b' {
  return (fen || '').split(' ')[1] === 'b' ? 'b' : 'w';
}

function colorToShort(color: 'white' | 'black'): 'w' | 'b' {
  return color === 'white' ? 'w' : 'b';
}

function adaptSearchOptions(
  fen: string,
  plyIndex: number,
  mode: BrowserAnalysisMode,
  purpose: 'screen' | 'deepen',
): AnalyzeFenOptions {
  const base = ANALYSIS_CONFIGS[mode][purpose];
  let legalCount = 32;
  let pieceCount = 32;
  try {
    const board = new Chess(fen);
    legalCount = board.moves().length;
    pieceCount = board.board().flat().filter(Boolean).length;
  } catch {
    /* keep defaults */
  }

  const opts = {...base};
  const inOpening = plyIndex <= 12;
  const simplePosition = pieceCount <= 12 || legalCount <= 10;
  if (mode === 'deep') {
    // Deep mode is used as evalengine cache input, so keep it aligned with
    // server Stockfish settings instead of adapting by phase/device.
    return opts;
  }

  if (purpose === 'screen') {
    if (inOpening && !simplePosition) {
      opts.depth = Math.max(8, (opts.depth || 10) - 1);
      opts.movetime = Math.max(300, (opts.movetime || 450) - 100);
    } else if (simplePosition) {
      opts.depth = (opts.depth || 10) + 2;
    }
  } else if (simplePosition) {
    opts.depth = (opts.depth || 14) + 2;
    opts.movetime = Math.min((opts.movetime || 1400) + 400, 3200);
  }
  return opts;
}

function uciToSan(fen: string, uci: string): string {
  if (!uci || uci.length < 4) {
    return '';
  }
  try {
    const b = new Chess(fen);
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const promotion = uci.length > 4 ? uci.slice(4, 5) : undefined;
    const mv = b.move(promotion ? {from, to, promotion} : {from, to}, {
      strict: false,
    } as any);
    return (mv as any)?.san || '';
  } catch {
    return '';
  }
}

function moveToUci(move: any): string {
  return `${move.from}${move.to}${move.promotion || ''}`;
}

function normalizeSan(san: string): string {
  return String(san || '').replace(/[+#?!]+/g, '');
}

function playedInResponse(move: string, response: string[]): boolean {
  const normalized = normalizeSan(move);
  return response.some(candidate => normalizeSan(candidate) === normalized);
}

function firstSanFromLine(line: string): string {
  return (
    String(line || '')
      .trim()
      .split(/\s+/)[0] || ''
  );
}

// Render a SAN principal variation as a move-numbered string, matching the
// format python-chess `board.variation_san()` produces on the backend, e.g.
//   white to move at move 2 -> "2. Nf3 Nc6 3. d4 exd4"
//   black to move at move 1 -> "1...c5 2. Nf3 d6"
// The backend/GPT hint pipeline (get_moves_string, best_line parsing, etc.)
// assumes this numbered format, so browser-generated best_possible_response /
// best_current_move must match it rather than emitting bare SAN.
function toNumberedVariation(sanMoves: string[], fen: string): string {
  if (!sanMoves || sanMoves.length === 0) {
    return '';
  }
  const parts = String(fen || '').split(' ');
  let moveNumber = parseInt(parts[5], 10);
  if (!Number.isFinite(moveNumber) || moveNumber < 1) {
    moveNumber = 1;
  }
  let whiteToMove = parts[1] !== 'b';
  const out: string[] = [];
  let i = 0;
  if (!whiteToMove) {
    out.push(`${moveNumber}...${sanMoves[0]}`);
    i = 1;
    moveNumber += 1;
    whiteToMove = true;
  }
  for (; i < sanMoves.length; i += 1) {
    if (whiteToMove) {
      out.push(`${moveNumber}. ${sanMoves[i]}`);
      whiteToMove = false;
    } else {
      out.push(sanMoves[i]);
      whiteToMove = true;
      moveNumber += 1;
    }
  }
  return out.join(' ');
}

function lineScoreForUser(
  line: EngineEvalLine,
  stm: 'w' | 'b',
  userColor: 'w' | 'b',
): number {
  return toUserScore({kind: line.kind, val: line.val}, stm, userColor);
}

function buildPlayers(meta: GameMeta): any {
  const meIsWhite = meta.color === 'white';
  return {
    white: meIsWhite ? meta.username : meta.opponent,
    black: meIsWhite ? meta.opponent : meta.username,
    white_rating: meIsWhite ? meta.player_rating : meta.opponent_rating,
    black_rating: meIsWhite ? meta.opponent_rating : meta.player_rating,
  };
}

function deriveGameResult(meta: GameMeta, finalScore: number): string {
  if (meta.raw?.result) {
    return meta.raw.result;
  }
  if (finalScore >= 200) {
    return '1-0';
  }
  if (finalScore <= -200) {
    return '0-1';
  }
  return '1/2-1/2';
}

function roundedTenths(value: number | undefined): number | undefined {
  return typeof value === 'number' && Number.isFinite(value)
    ? Math.round(value * 10) / 10
    : undefined;
}

function lastPlyForColor(
  plies: Ply[],
  color: 'white' | 'black',
): Ply | undefined {
  for (let i = plies.length - 1; i >= 0; i -= 1) {
    if (plies[i].color === color) {
      return plies[i];
    }
  }
  return undefined;
}

function averageTimeForColor(
  plies: Ply[],
  color: 'white' | 'black',
): number | undefined {
  const timed = plies.filter(
    ply => ply.color === color && typeof ply.timeTakenInSecs === 'number',
  );
  if (!timed.length) {
    return undefined;
  }
  return roundedTenths(
    timed.reduce((sum, ply) => sum + (ply.timeTakenInSecs || 0), 0) /
      timed.length,
  );
}

function castlingMoveForColor(plies: Ply[], color: 'white' | 'black'): number {
  return (
    plies.find(
      ply => ply.color === color && /^(O-O|0-0)(-O|-0)?[+#]?$/.test(ply.san),
    )?.moveIndex || 0
  );
}

function timeUtilizedPercent(
  plies: Ply[],
  color: 'white' | 'black',
  initialSeconds?: number,
): number | undefined {
  if (typeof initialSeconds !== 'number' || initialSeconds <= 0) {
    return undefined;
  }
  const spent = plies
    .filter(ply => ply.color === color)
    .reduce((sum, ply) => sum + (ply.timeTakenInSecs || 0), 0);
  return roundedTenths((spent / initialSeconds) * 100);
}

export function buildGameOverview(meta: GameMeta, plies: Ply[]): any {
  const userColor = meta.color;
  const opponentColor = userColor === 'white' ? 'black' : 'white';
  const userLast = lastPlyForColor(plies, userColor);
  const opponentLast = lastPlyForColor(plies, opponentColor);
  const timeControl = parseTimeControl(
    meta.time_control || meta.raw?.time_control || '',
  );
  const userDurationSeconds = plies
    .filter(ply => ply.color === userColor)
    .reduce((sum, ply) => sum + (ply.timeTakenInSecs || 0), 0);
  const opponentDurationSeconds = plies
    .filter(ply => ply.color === opponentColor)
    .reduce((sum, ply) => sum + (ply.timeTakenInSecs || 0), 0);
  const totalDurationSeconds =
    userDurationSeconds || opponentDurationSeconds
      ? userDurationSeconds + opponentDurationSeconds
      : undefined;

  return {
    ...meta.raw,
    id: meta.raw?.id,
    uuid: meta.uuid || meta.raw?.uuid,
    platform: meta.platform || meta.raw?.platform,
    url: meta.raw?.url || meta.raw?.game_url,
    color: userColor === 'white' ? 'White' : 'Black',
    pgn: meta.pgn,
    username: meta.username,
    opponent_username: meta.opponent,
    rating: meta.player_rating ?? meta.raw?.rating,
    opponent_rating: meta.opponent_rating ?? meta.raw?.opponent_rating,
    time_control: meta.time_control || meta.raw?.time_control,
    time_class: meta.time_class || meta.raw?.time_class,
    result: meta.raw?.result,
    total_number_of_moves:
      meta.raw?.total_number_of_moves || Math.ceil(plies.length / 2),
    castling_index:
      meta.raw?.castling_index ?? castlingMoveForColor(plies, userColor),
    opponent_castling_index:
      meta.raw?.opponent_castling_index ??
      castlingMoveForColor(plies, opponentColor),
    average_time:
      meta.raw?.average_time ?? averageTimeForColor(plies, userColor),
    opponent_average_time:
      meta.raw?.opponent_average_time ??
      averageTimeForColor(plies, opponentColor),
    time_left_on_clock:
      meta.raw?.time_left_on_clock ?? userLast?.timeLeftOnClock,
    opponent_time_left_on_clock:
      meta.raw?.opponent_time_left_on_clock ?? opponentLast?.timeLeftOnClock,
    time_utilized:
      meta.raw?.time_utilized ??
      timeUtilizedPercent(plies, userColor, timeControl.initialSeconds),
    opponent_time_utilized:
      meta.raw?.opponent_time_utilized ??
      timeUtilizedPercent(plies, opponentColor, timeControl.initialSeconds),
    total_game_duration_in_minutes:
      meta.raw?.total_game_duration_in_minutes ??
      (typeof totalDurationSeconds === 'number'
        ? roundedTenths(totalDurationSeconds / 60)
        : undefined),
  };
}

async function analyzePosition(
  pool: EnginePool,
  fen: string,
  opts: AnalyzeFenOptions,
  userColor: 'w' | 'b',
): Promise<StoredEval> {
  try {
    const board = new Chess(fen);
    const terminalScore = terminalCheckmateUserScore(fen, userColor);
    if (terminalScore !== null) {
      return {
        userScore: terminalScore,
        bestUci: '',
        pvSan: [],
        lines: [],
        stm: stmOf(fen),
      };
    }
    const legalMoves = board.moves({verbose: true}) as any[];
    if (legalMoves.length === 1) {
      const forced = legalMoves[0];
      board.move(
        forced.promotion
          ? {from: forced.from, to: forced.to, promotion: forced.promotion}
          : {from: forced.from, to: forced.to},
        {strict: false} as any,
      );
      const child = await pool.run(board.fen(), opts);
      const stored = evalToStored(child, board.fen(), userColor);
      const forcedScore = child.best1
        ? {kind: child.best1.kind, val: -child.best1.val}
        : null;
      const forcedStm = stmOf(fen);
      const forcedUserScore = toUserScore(forcedScore, forcedStm, userColor);
      const forcedSan = forced.san || '';
      return {
        ...stored,
        userScore: forcedUserScore,
        bestUci: moveToUci(forced),
        pvSan: [forcedSan, ...stored.pvSan].filter(Boolean),
        lines: [
          {
            rank: 1,
            kind: child.best1?.kind || 'cp',
            val: forcedScore?.val || 0,
            depth: child.best1?.depth || 0,
            san: [forcedSan, ...stored.pvSan].filter(Boolean),
            pvUci: [moveToUci(forced), ...(child.lines[0]?.pvUci || [])].slice(
              0,
              MAX_BROWSER_CACHE_PV_MOVES,
            ),
            scoreCp: forcedUserScore,
          },
        ],
        stm: forcedStm,
      };
    }
  } catch {
    // Fall through to a normal engine search when chess.js rejects an edge FEN.
  }

  const result = await pool.run(fen, opts);
  return evalToStored(result, fen, userColor);
}

export function terminalCheckmateUserScore(
  fen: string,
  userColor: 'w' | 'b',
): number | null {
  try {
    const board = new Chess(fen);
    if (!board.isCheckmate()) {
      return null;
    }
    const sideToMove = stmOf(fen);
    const whiteScore = sideToMove === 'w' ? -MATE_SCORE_CP : MATE_SCORE_CP;
    return userColor === 'w' ? whiteScore : -whiteScore;
  } catch {
    return null;
  }
}

function evalToStored(
  result: PositionEval,
  fen: string,
  userColor: 'w' | 'b',
): StoredEval {
  const stm = stmOf(fen);
  return {
    userScore: toUserScore(result.best1, stm, userColor),
    bestUci: result.bestUci,
    pvSan: result.pvSan,
    lines: result.lines,
    stm,
  };
}

function buildMove(
  meta: GameMeta,
  gameId: number | string,
  ply: Ply,
  evals: StoredEval[],
  index: number,
  expectedResponseFirstMoves: string[],
): MoveBuild {
  const player = ply.color === meta.color ? 'me' : 'opponent';
  const score = evals[index].userScore;
  const last = evals[index - 1].userScore;
  const scoreChange = score - last;
  const bestSan = uciToSan(ply.fenBefore, evals[index - 1].bestUci);
  const lines = evals[index - 1].lines
    .map(line => ({
      ...line,
      scoreCp: lineScoreForUser(
        line,
        evals[index - 1].stm,
        colorToShort(meta.color),
      ),
    }))
    .sort((a, b) => a.rank - b.rank);
  const bestScoreCp = lines[0]?.scoreCp ?? evals[index - 1].userScore;
  const bestResponseLines = selectBestResponses(lines, bestScoreCp, last);
  const bestResponses = bestResponseLines.map(line => line.san.join(' '));
  const bestResponseFirstMoves = bestResponseLines
    .map(line => line.san[0])
    .filter(Boolean);
  let cls = classify({
    player,
    score,
    scoreChange,
    lastScore: last,
  });
  if (
    (cls.inaccuracy || cls.mistake || cls.blunder) &&
    playedInResponse(ply.san, expectedResponseFirstMoves)
  ) {
    cls = {inaccuracy: 0, mistake: 0, blunder: 0};
  }
  const moveColor = colorToShort(ply.color);
  const puzzle = classifyPuzzle({
    player,
    ply: ply.ply,
    moveIndex: ply.moveIndex,
    cls,
    lastScore: last,
    colorToMove: stmOf(ply.fenBefore),
    moveColor,
    playedInBestResponse: playedInResponse(ply.san, bestResponseFirstMoves),
  });

  return {
    cls,
    puzzle,
    moveFields: {
      id: undefined as number | undefined,
      uuid: meta.uuid,
      userid: meta.userid,
      platform: meta.platform,
      username: meta.username,
      opponent: meta.opponent,
      move: ply.san,
      fen: ply.fenAfter,
      move_index: ply.moveIndex,
      player,
      color: ply.color,
      score,
      score_change: scoreChange,
      inaccuracy: cls.inaccuracy,
      mistake: cls.mistake,
      blunder: cls.blunder,
      best_move: bestSan,
      // Move-numbered PV lines, matching the backend/GPT format (test.json).
      // best_current_move = the mover's own best line from the pre-move
      // position; best_possible_response = the opponent's reply line from the
      // post-move position.
      best_current_move: toNumberedVariation(
        evals[index - 1].pvSan,
        ply.fenBefore,
      ),
      best_possible_response: toNumberedVariation(
        evals[index].pvSan,
        ply.fenAfter,
      ),
      // best_response is the opponent's best REPLY to this move: the first move
      // of the post-move principal variation. This matches the legacy/backend
      // semantics (test.json) and is what puzzle generation/verification
      // consume. It is intentionally NOT bestResponses[0], which is the mover's
      // own best line for this turn (already exposed as best_current_move /
      // best_move). bestResponses (below) stays as the pre-move candidate set
      // because move classification depends on it.
      best_response: evals[index].pvSan[0] || '',
      best_responses: bestResponses,
      alternate_move: null,
      alternate_moves: null,
      opening_principle_insights: [],
      reason: null,
      time_taken_in_secs: ply.timeTakenInSecs,
      time_left_on_clock: ply.timeLeftOnClock,
      average_time: ply.averageTime,
      previous_fen: ply.fenBefore,
      source: 'browser',
      game_uuid: gameId,
    },
  };
}

function buildPuzzleSummary(moves: MoveBuild[]): any {
  const puzzles = moves.filter(move => move.puzzle.isPuzzle);
  const byStage = puzzles.reduce((acc, move) => {
    const stage = move.puzzle.stage || 'middlegame';
    acc[stage] = (acc[stage] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  return {
    total: puzzles.length,
    opening: byStage.opening || 0,
    middlegame: byStage.middlegame || 0,
    endgame: byStage.endgame || 0,
  };
}

export function isBrowserAnalysisSupported(): boolean {
  return isEngineSupported();
}

export function analyzeGameInBrowser(
  gameId: number | string,
  poolSize = 1,
  mode: BrowserAnalysisMode = 'normal',
): Observable<AnalysisResponse> {
  return new Observable<AnalysisResponse>(observer => {
    let cancelled = false;
    let positionsTimedOut = 0;
    let analysisStartedAt = Date.now();
    const pool = new EnginePool();
    const emitted: AnalysisResponse[] = [];
    const emit = (response: AnalysisResponse) => {
      emitted.push(response);
      observer.next(response);
    };

    (async () => {
      let meta: GameMeta | null = null;
      let plies: Ply[] = [];
      try {
        meta = await fetchGameMeta(gameId);
        if (cancelled) {
          return;
        }
        if (!meta) {
          throw new Error('browser analysis: no PGN available for game');
        }

        plies = parsePliesFromPgn(meta.pgn);
        if (!plies.length) {
          throw new Error('browser analysis: PGN produced no moves');
        }

        analysisStartedAt = Date.now();
        reportBrowserAnalysisEvent(
          {
            event: 'attempt',
            mode,
            gameUuid: meta?.uuid ?? String(gameId),
            userId: meta?.userid,
            positionsTotal: plies.length,
          },
          meta?.username,
        );

        const deepProfile = mode === 'deep' ? readDeepDeviceProfile() : null;
        const configHash =
          mode === 'deep' && deepProfile
            ? deepConfigHash(deepProfile)
            : ANALYSIS_CONFIGS[mode].configHash;
        const cacheKey = analysisCacheKey(
          meta.uuid || gameId,
          ENGINE_VERSION,
          configHash,
        );
        const cached = await getCached(cacheKey);
        if (cached && !cancelled) {
          cached.forEach(response => observer.next(response));
          observer.complete();
          return;
        }

        const userColor = colorToShort(meta.color);
        const players = buildPlayers(meta);
        const totalMoves = plies.length;
        const positions = [plies[0].fenBefore, ...plies.map(p => p.fenAfter)];
        const gameOverview = buildGameOverview(meta, plies);

        emit({
          source: 'browser',
          analysis_mode: mode,
          players,
          game_details: {
            time_control: meta.time_control,
            type: meta.time_class,
            uuid: meta.uuid,
          },
        });
        emit({
          source: 'browser',
          analysis_mode: mode,
          game_overview: gameOverview,
        });
        emit({
          source: 'browser',
          analysis_mode: mode,
          game_reviewed_by_user: false,
        });
        emit({
          source: 'browser',
          analysis_mode: mode,
          game_result: deriveGameResult(meta, 0),
        });
        emit({
          source: 'browser',
          analysis_mode: mode,
          status: 'starting_analysis',
        });

        await pool.init(poolSize);
        if (cancelled) {
          return;
        }

        const evals: StoredEval[] = new Array(positions.length);
        const activeDeepProfile = deepProfile || readDeepDeviceProfile();
        const applyDeepDeviceProfile = (
          opts: AnalyzeFenOptions,
          purpose: 'screen' | 'deepen',
        ): AnalyzeFenOptions => {
          if (mode !== 'deep') {
            return opts;
          }
          const depth =
            purpose === 'deepen'
              ? Math.min(
                  opts.depth || activeDeepProfile.deepenDepth,
                  activeDeepProfile.deepenDepth,
                )
              : opts.depth;
          return {
            ...opts,
            depth,
            multiPV:
              purpose === 'deepen' && depth <= 24
                ? Math.min(opts.multiPV || 1, 1)
                : opts.multiPV,
          };
        };
        const reduceDeepDeviceDepth = (
          purpose: 'screen' | 'deepen',
          idx: number,
        ): boolean => {
          if (mode !== 'deep') {
            return false;
          }
          const before =
            purpose === 'deepen'
              ? activeDeepProfile.deepenDepth
              : ANALYSIS_CONFIGS.deep.screen.depth || 20;
          const next =
            purpose === 'deepen'
              ? Math.max(MIN_DEEPEN_DEPTH, before - 4)
              : before;
          if (next >= before) {
            return false;
          }
          if (purpose === 'deepen') {
            activeDeepProfile.deepenDepth = next;
          }
          writeDeepDeviceProfile(activeDeepProfile);
          emit({
            source: 'browser',
            analysis_mode: mode,
            status: 'analysis_progress',
            analysis_progress: Math.max(
              0,
              Math.min(0.95, idx / Math.max(positions.length - 1, 1)),
            ),
            current_position: idx + 1,
            total_positions: positions.length,
            progress_message: `Browser is slow here; reducing ${purpose} depth to ${next}`,
          });
          return true;
        };
        const progressForPly = (ply: number): number => {
          return Math.max(0, Math.min(0.95, ply / Math.max(plies.length, 1)));
        };
        const analyzeWithAdaptiveDepth = async (
          idx: number,
          purpose: 'screen' | 'deepen',
        ): Promise<StoredEval> => {
          let attempts = 0;
          while (true) {
            const opts = applyDeepDeviceProfile(
              adaptSearchOptions(positions[idx], idx, mode, purpose),
              purpose,
            );
            try {
              return await analyzePosition(
                pool,
                positions[idx],
                opts,
                userColor,
              );
            } catch (err) {
              if (
                err instanceof Error &&
                err.message === PER_POSITION_TIMEOUT_MESSAGE
              ) {
                positionsTimedOut += 1;
              }
              if (
                mode !== 'deep' ||
                purpose === 'screen' ||
                attempts >= 3 ||
                !reduceDeepDeviceDepth(purpose, idx)
              ) {
                throw err;
              }
              attempts += 1;
            }
          }
        };
        const analyzeScreenPositionAt = async (idx: number) => {
          if (evals[idx]) {
            return;
          }
          emit({
            source: 'browser',
            analysis_mode: mode,
            status: 'analysis_progress',
            analysis_progress: progressForPly(Math.min(idx + 1, plies.length)),
            current_position: Math.min(idx + 1, positions.length),
            total_positions: positions.length,
            progress_message:
              mode === 'deep'
                ? `Analyzing move ${Math.min(idx + 1, plies.length)}/${
                    plies.length
                  }`
                : `Analyzing ${idx + 1}/${positions.length}`,
          });
          evals[idx] = await analyzeWithAdaptiveDepth(idx, 'screen');
        };
        let completedScreenPositions = 0;
        const screenReady: Promise<void>[] =
          mode === 'deep'
            ? []
            : positions.map((fen, idx) => {
                const p = analyzePosition(
                  pool,
                  fen,
                  adaptSearchOptions(fen, idx, mode, 'screen'),
                  userColor,
                ).then(e => {
                  evals[idx] = e;
                  completedScreenPositions += 1;
                  emit({
                    source: 'browser',
                    analysis_mode: mode,
                    status: 'analysis_progress',
                    analysis_progress: Math.max(
                      0,
                      Math.min(
                        0.95,
                        completedScreenPositions /
                          Math.max(positions.length, 1),
                      ),
                    ),
                    current_position: completedScreenPositions,
                    total_positions: positions.length,
                    progress_message: `Analyzing ${completedScreenPositions}/${positions.length}`,
                  });
                });
                // Mark handled so a rejection arriving after cancel / pool
                // destroy (when this promise may never be awaited) doesn't
                // surface as an unhandled rejection. The awaited reference
                // returned below still rejects for the normal error path.
                p.catch(err => {
                  if (
                    err instanceof Error &&
                    err.message === PER_POSITION_TIMEOUT_MESSAGE
                  ) {
                    positionsTimedOut += 1;
                  }
                });
                return p;
              });

        const builtMoves: MoveBuild[] = [];
        let expectedResponseFirstMoves: string[] = [];
        for (let i = 1; i <= plies.length; i += 1) {
          if (mode === 'deep') {
            await Promise.all([
              analyzeScreenPositionAt(i - 1),
              analyzeScreenPositionAt(i),
            ]);
          } else {
            await Promise.all([screenReady[i - 1], screenReady[i]]);
          }
          if (cancelled) {
            return;
          }

          const player = plies[i - 1].color === meta.color ? 'me' : 'opponent';
          const score = evals[i].userScore;
          const last = evals[i - 1].userScore;
          const provisionalItem = {
            player,
            cls: classify({
              player,
              score,
              scoreChange: score - last,
              lastScore: last,
            }),
            scoreChange: score - last,
            lastScore: last,
          };

          const shouldRunDeepen =
            mode === 'deep' ? false : shouldDeepen(provisionalItem);

          if (shouldRunDeepen) {
            emit({
              source: 'browser',
              analysis_mode: mode,
              status: 'analysis_progress',
              analysis_progress: progressForPly(i),
              current_position: i,
              total_positions: plies.length,
              progress_message:
                mode === 'deep'
                  ? `Deepening move ${i}/${plies.length}`
                  : `Checking move ${i}/${plies.length}`,
            });
            const beforeOptions = adaptSearchOptions(
              positions[i - 1],
              i - 1,
              mode,
              'deepen',
            );
            const afterOptions = adaptSearchOptions(
              positions[i],
              i,
              mode,
              'deepen',
            );
            try {
              const deepBefore =
                mode === 'deep'
                  ? await analyzeWithAdaptiveDepth(i - 1, 'deepen')
                  : await analyzePosition(
                      pool,
                      positions[i - 1],
                      beforeOptions,
                      userColor,
                    );
              evals[i - 1] = deepBefore;
            } catch (err) {
              if (mode !== 'deep') {
                throw err;
              }
              emit({
                source: 'browser',
                analysis_mode: mode,
                status: 'analysis_progress',
                analysis_progress: progressForPly(i),
                current_position: i,
                total_positions: plies.length,
                progress_message: `Skipped extra depth before move ${i}; continuing`,
              });
            }
            try {
              const deepAfter =
                mode === 'deep'
                  ? await analyzeWithAdaptiveDepth(i, 'deepen')
                  : await analyzePosition(
                      pool,
                      positions[i],
                      afterOptions,
                      userColor,
                    );
              evals[i] = deepAfter;
            } catch (err) {
              if (mode !== 'deep') {
                throw err;
              }
              emit({
                source: 'browser',
                analysis_mode: mode,
                status: 'analysis_progress',
                analysis_progress: progressForPly(i),
                current_position: i,
                total_positions: plies.length,
                progress_message: `Skipped extra depth after move ${i}; continuing`,
              });
            }
            if (cancelled) {
              return;
            }
          }

          const built = buildMove(
            meta,
            gameId,
            plies[i - 1],
            evals,
            i,
            expectedResponseFirstMoves,
          );
          builtMoves.push(built);
          expectedResponseFirstMoves = (built.moveFields.best_responses || [])
            .map(firstSanFromLine)
            .filter(Boolean);
          emit({
            source: 'browser',
            analysis_mode: mode,
            game_uuid: gameId,
            move_index: plies[i - 1].moveIndex,
            total_moves: totalMoves,
            game_engine_analyzed: true,
            analysis: built.moveFields,
          });
        }

        const gameMoves = builtMoves.map(move => move.moveFields);
        const browserEngineCache = buildBrowserEngineCache(plies, evals, mode);
        const userMoves = builtMoves.filter(
          move => move.moveFields.player === 'me',
        );
        const opponentMoves = builtMoves.filter(
          move => move.moveFields.player === 'opponent',
        );
        const userInacc = userMoves.reduce(
          (sum, move) => sum + move.cls.inaccuracy,
          0,
        );
        const userMist = userMoves.reduce(
          (sum, move) => sum + move.cls.mistake,
          0,
        );
        const userBlun = userMoves.reduce(
          (sum, move) => sum + move.cls.blunder,
          0,
        );
        const opponentInacc = opponentMoves.reduce(
          (sum, move) => sum + move.cls.inaccuracy,
          0,
        );
        const opponentMist = opponentMoves.reduce(
          (sum, move) => sum + move.cls.mistake,
          0,
        );
        const opponentBlun = opponentMoves.reduce(
          (sum, move) => sum + move.cls.blunder,
          0,
        );
        // Server-parity accuracy (matches start_analyzing_game): each side's
        // accuracy is derived from its own inaccuracy/mistake/blunder counts via
        //   100 − ((blunders·2.5 + mistakes·1.0 + inaccuracies·0.5) / moves)·100
        // (clamped 50–100). Zero errors ⇒ exactly 100%, matching the server.
        const computedAccuracy = accuracy(
          userInacc,
          userMist,
          userBlun,
          userMoves.length,
        );
        const opponentAccuracy = accuracy(
          opponentInacc,
          opponentMist,
          opponentBlun,
          opponentMoves.length,
        );
        const puzzleSummary = buildPuzzleSummary(builtMoves);
        const finalResult = deriveGameResult(
          meta,
          evals[evals.length - 1].userScore,
        );
        const finalGameOverview = {
          ...gameOverview,
          accuracy: computedAccuracy,
          opponent_accuracy: opponentAccuracy,
        };

        emit({
          source: 'browser',
          analysis_mode: mode,
          game_score_details: {
            average_accuracy: computedAccuracy,
            opponent_average_accuracy: opponentAccuracy,
            no_of_blunders: userBlun,
            no_of_mistakes: userMist,
            no_of_inaccuracies: userInacc,
            opponent_no_of_blunders: opponentBlun,
            opponent_no_of_mistakes: opponentMist,
            opponent_no_of_inaccuracies: opponentInacc,
            count: {
              blunder_in_less_than_ten_secs: 0,
              first_attempt_puzzles: 0,
            },
          },
        });

        emit({
          source: 'browser',
          analysis_mode: mode,
          game_result: finalResult,
        });
        emit({
          source: 'browser',
          analysis_mode: mode,
          puzzle_summary: puzzleSummary,
        });
        emit({
          source: 'browser',
          analysis_mode: mode,
          game_engine_analyzed: true,
        });
        emit({
          source: 'browser',
          analysis_mode: mode,
          game_uuid: gameId,
          status: 'Game already analyzed',
          players,
          game_details: {
            time_control: meta.time_control,
            type: meta.time_class,
            uuid: meta.uuid,
          },
          game_overview: finalGameOverview,
          game_result: finalResult,
          puzzle_summary: puzzleSummary,
          game_score_details: {
            average_accuracy: computedAccuracy,
            opponent_average_accuracy: opponentAccuracy,
            no_of_blunders: userBlun,
            no_of_mistakes: userMist,
            no_of_inaccuracies: userInacc,
            opponent_no_of_blunders: opponentBlun,
            opponent_no_of_mistakes: opponentMist,
            opponent_no_of_inaccuracies: opponentInacc,
            count: {
              blunder_in_less_than_ten_secs: 0,
              first_attempt_puzzles: 0,
            },
          },
          game_engine_analyzed: true,
          total_moves: totalMoves,
          game_moves: gameMoves,
        });

        const finalCacheKey =
          mode === 'deep'
            ? analysisCacheKey(
                meta.uuid || gameId,
                ENGINE_VERSION,
                deepConfigHash(activeDeepProfile),
              )
            : cacheKey;
        await putCached(finalCacheKey, emitted);
        // Persist to the chess backend as browser-partial analysis (analyzed=2).
        // Deep mode also stores compact engine cache data for later premium eval.
        // Fire-and-forget: the user already saw the analysis.
        persistBrowserAnalysisToBackend(
          meta.uuid || gameId,
          gameMoves,
          computedAccuracy,
          opponentAccuracy,
          mode,
          browserEngineCache,
        );
        reportBrowserAnalysisEvent(
          {
            event: 'success',
            mode,
            gameUuid: meta?.uuid ?? String(gameId),
            userId: meta?.userid,
            durationMs: Date.now() - analysisStartedAt,
            positionsTotal: plies.length,
            positionsDone: plies.length,
          },
          meta?.username,
        );
        observer.complete();
      } catch (err) {
        // Snapshot BEFORE observer.error: RxJS runs the subscriber teardown
        // synchronously when observer.error fires, which sets `cancelled = true`.
        // Reading `cancelled` after that would mislabel real engine errors as
        // 'cancelled' and hide them from the failure-rate/affected-user views.
        const wasUserCancelled = cancelled;
        const normalizedErr =
          err instanceof Error ? err : new Error('browser analysis failed');
        if (!wasUserCancelled) {
          observer.error(normalizedErr);
        }
        const reason = wasUserCancelled
          ? 'cancelled'
          : classifyFailure(normalizedErr, {
              positionsTotal: plies.length,
              positionsTimedOut,
            });
        reportBrowserAnalysisEvent(
          {
            event: wasUserCancelled ? 'cancelled' : 'failure',
            mode,
            gameUuid: meta ? meta.uuid ?? String(gameId) : String(gameId),
            userId: meta?.userid,
            reason,
            message: normalizedErr.message,
            durationMs: Date.now() - analysisStartedAt,
            positionsTotal: plies.length > 0 ? plies.length : undefined,
            positionsDone: positionsTimedOut,
          },
          meta?.username,
        );
      } finally {
        pool.destroy();
      }
    })();

    return () => {
      cancelled = true;
      pool.destroy();
    };
  });
}
